import React from "react";

const MERCHANT_GROUPS = ["COMMERCIAL_REGISTER", "TAX_CARD"];

const DocumentCard = ({ label, available, loading, onView, text }) => (
  <article className="registration-document-card">
    <div className="registration-document-icon" aria-hidden="true">
      <i className="fa-regular fa-image" />
    </div>
    <div className="registration-document-copy">
      <h4>{label}</h4>
      <p className={available ? "is-available" : "is-missing"}>
        {available ? text.documentUploaded : text.documentMissing}
      </p>
    </div>
    {available && onView && (
      <button
        type="button"
        className="registration-document-view"
        aria-label={text.viewDocument}
        disabled={loading}
        onClick={onView}
      >
        <span>{loading ? text.openingDocument : text.viewDocument}</span>
        <i className="fa-solid fa-expand" aria-hidden="true" />
      </button>
    )}
  </article>
);

const groupLabel = (group, text) => text.reviewGroups[group] || group;

const submissionGroupGalleryItems = (submission, group, text) =>
  (group.files || []).map((file, index) => ({
    key: `${submission.id}:${group.group}:${file.id}`,
    kind: "submission",
    submissionId: submission.id,
    group: group.group,
    fileId: file.id,
    title:
      file.originalFileName ||
      `${groupLabel(group.group, text)} ${index + 1}`
  }));

const openSubmissionItem = ({
  submission,
  group,
  file,
  label,
  galleryItems,
  galleryIndex,
  onViewGallery,
  onViewFile
}) => onViewGallery
  ? onViewGallery(galleryItems, galleryIndex)
  : onViewFile?.(submission, group.group, file, label);

const SubmissionGroup = ({
  submission,
  group,
  onViewGallery,
  onViewFile,
  loading,
  text,
  footer
}) => {
  const galleryItems = submissionGroupGalleryItems(submission, group, text);
  return (
    <section
      className="registration-submission-group registration-review-document-group"
      data-document-group={group.group}
    >
      <header>
        <h4>{groupLabel(group.group, text)}</h4>
      </header>
      <div className="registration-document-list">
        {(group.files || []).map((file, index) => {
          const label =
            file.originalFileName || `${groupLabel(group.group, text)} ${index + 1}`;
          const itemKey = `${submission.id}:${group.group}:${file.id}`;
          const galleryIndex = galleryItems.findIndex((item) => item.key === itemKey);
          return (
            <DocumentCard
              key={file.id || `${group.group}-${index}`}
              label={label}
              available
              loading={loading === itemKey}
              onView={() => openSubmissionItem({
                submission,
                group,
                file,
                label,
                galleryItems,
                galleryIndex,
                onViewGallery,
                onViewFile
              })}
              text={text}
            />
          );
        })}
      </div>
      {footer?.(group.group, {
        reviewStatus: group.reviewStatus,
        source: "submission"
      })}
    </section>
  );
};

const legacyDefinitions = (type, text) => type === "customers"
  ? [
      { key: "frontFile", group: "NATIONAL_ID", label: text.nationalIdFront },
      { key: "backFile", group: "NATIONAL_ID", label: text.nationalIdBack }
    ]
  : [
      {
        key: "commercialRegister",
        group: "COMMERCIAL_REGISTER",
        label: text.commercialRegister
      },
      { key: "taxCard", group: "TAX_CARD", label: text.taxCard }
    ];

const legacyGalleryItem = (definition, metadata) => ({
  key: definition.key,
  kind: "legacy",
  documentType: metadata?.documentType || definition.key,
  title: definition.label
});

const LegacyDocumentCard = ({
  definition,
  metadata,
  galleryItems,
  onViewGallery,
  onViewLegacy,
  loading,
  text
}) => {
  const available = Boolean(metadata?.exists || metadata?.uploaded);
  const galleryIndex = galleryItems.findIndex((item) => item.key === definition.key);
  const canView = available && metadata?.canViewDocument;
  return (
    <DocumentCard
      label={definition.label}
      available={available}
      loading={loading === definition.key}
      onView={canView
        ? () => onViewGallery
          ? onViewGallery(galleryItems, galleryIndex)
          : onViewLegacy?.(metadata?.documentType || definition.key, definition.label)
        : null}
      text={text}
    />
  );
};

const MerchantDocuments = ({
  submission,
  legacyMetadata,
  onViewGallery,
  onViewFile,
  onViewLegacy,
  renderGroupFooter,
  loading,
  text
}) => {
  const definitions = legacyDefinitions("merchants", text);
  return (
    <div className="registration-submission-groups is-merchant">
      {MERCHANT_GROUPS.map((groupCode) => {
        const submissionGroup = submission?.groups?.find(
          (group) => group.group === groupCode
        );
        if (submissionGroup) {
          return (
            <SubmissionGroup
              key={groupCode}
              submission={submission}
              group={submissionGroup}
              onViewGallery={onViewGallery}
              onViewFile={onViewFile}
              loading={loading}
              text={text}
              footer={renderGroupFooter}
            />
          );
        }

        const definition = definitions.find((item) => item.group === groupCode);
        const metadata = legacyMetadata?.[definition.key] || {};
        const galleryItems = metadata.canViewDocument &&
          (metadata.exists || metadata.uploaded)
          ? [legacyGalleryItem(definition, metadata)]
          : [];
        return (
          <section
            key={groupCode}
            className="registration-submission-group registration-review-document-group"
            data-document-group={groupCode}
          >
            <header><h4>{groupLabel(groupCode, text)}</h4></header>
            <div className="registration-document-list">
              <LegacyDocumentCard
                definition={definition}
                metadata={metadata}
                galleryItems={galleryItems}
                onViewGallery={onViewGallery}
                onViewLegacy={onViewLegacy}
                loading={loading}
                text={text}
              />
            </div>
            {renderGroupFooter?.(groupCode, {
              reviewStatus: metadata.status,
              source: "existing"
            })}
          </section>
        );
      })}
    </div>
  );
};

const CustomerDocuments = ({
  submission,
  legacyMetadata,
  onViewGallery,
  onViewFile,
  onViewLegacy,
  renderGroupFooter,
  loading,
  text
}) => {
  if (submission?.groups?.length) {
    return (
      <div className="registration-submission-groups">
        {submission.groups.map((group) => (
          <SubmissionGroup
            key={group.id || group.group}
            submission={submission}
            group={group}
            onViewGallery={onViewGallery}
            onViewFile={onViewFile}
            loading={loading}
            text={text}
            footer={renderGroupFooter}
          />
        ))}
      </div>
    );
  }

  const definitions = legacyDefinitions("customers", text);
  const galleryItems = definitions
    .filter((definition) => {
      const metadata = legacyMetadata?.[definition.key] || {};
      return Boolean(metadata.exists || metadata.uploaded) && metadata.canViewDocument;
    })
    .map((definition) => legacyGalleryItem(
      definition,
      legacyMetadata?.[definition.key] || {}
    ));
  const reviewStatus = definitions
    .map((definition) => legacyMetadata?.[definition.key]?.status)
    .find(Boolean);
  return (
    <section
      className="registration-submission-group registration-review-document-group"
      data-document-group="NATIONAL_ID"
    >
      <header><h4>{groupLabel("NATIONAL_ID", text)}</h4></header>
      <div className="registration-document-list">
        {definitions.map((definition) => (
          <LegacyDocumentCard
            key={definition.key}
            definition={definition}
            metadata={legacyMetadata?.[definition.key] || {}}
            galleryItems={galleryItems}
            onViewGallery={onViewGallery}
            onViewLegacy={onViewLegacy}
            loading={loading}
            text={text}
          />
        ))}
      </div>
      {renderGroupFooter?.("NATIONAL_ID", {
        reviewStatus,
        source: "existing"
      })}
    </section>
  );
};

const RegistrationReviewDocuments = ({ type, ...props }) =>
  type === "merchants"
    ? <MerchantDocuments {...props} />
    : <CustomerDocuments {...props} />;

export default RegistrationReviewDocuments;
